import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { MenuItem, TextField, css } from '@mui/material';
import { isSameDay } from 'date-fns';
import { debounce, isNil, sortBy } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useTokenContext } from 'src/account/shared/service/token.context';
import { showToastMessage } from 'src/coach/service/coach.utils';
import { useAsyncTaskStatus } from 'src/coach/ui/class-details/hooks/async-task-status';
import { Button, Modal } from 'src/shared';
import { useLoading } from 'src/shared/hooks/use-loading';
import { breakpoints, useMediaQuery } from 'src/shared/hooks/use-media-query';
import { useModal } from 'src/shared/hooks/use-modal';
import BlockLoading from 'src/shared/ui/components/block-loading/block-loading';
import FindCoachHeader from './components/header';
import {
  coachAvailabilityFiltersQuery,
  coachAvailabilityScheduleQuery,
  conflictingEventDatesQuery,
  franchiseeGetById,
  getClassEventDates,
  participantRolesQuery,
  scheduleCoachesMutation,
} from './graphql.js';
import {
  COACH_ASSIGNMENT_ACTION_ITEMS,
  COACH_ASSIGNMENT_ENUM,
  getCalendarItems,
  getCategoryOptions,
  getCoachAssignmentInput,
  getCoachConflicts,
} from './helpers.js';
import './style.module.scss';
import CoachDetails from './components/coach-details';
import CoachFilter from './components/coach-filter';
import DayCalendar from './components/day-calendar';
import EventDetails from './components/event-details';
import { NotifySelection } from './components/notify-selection';

const ModalWrapper = ({ $actions, children }) => {
  return (
    <Modal
      className="tw-w-full tw-max-h-full sm:tw-max-h-[calc(100% - 2rem)] sm:tw-max-w-[95rem]"
      $actions={$actions}
    >
      {children}
    </Modal>
  );
};

const datePickerStyle = (theme) => css`
  .react-datepicker__week {
    line-height: 0;
  }

  .react-datepicker__day-name {
    width: 33px;
    height: 21px;
  }

  .react-datepicker__day {
    transition: 0.2s;
    border-radius: ${theme.shape.rounded};
    width: 33px;
    height: 21px;

    line-height: initial;

    display: inline-flex;
    justify-content: center;
    align-items: center;

    pointer-events: none;
    user-select: none;
  }

  .react-datepicker__day--selected {
    background-color: ${theme.palette.primary.main};
    color: #fff;
    cursor: pointer;
    pointer-events: all;
  }

  .react-datepicker__day--disabled {
    background-color: #ccc;
    color: #000;
    cursor: pointer;
    pointer-events: all;
  }
`;

export const FindCoach = ({
  eventDate,
  eventDates = [],
  isBulk,
  onClose,
  onSelectCoach = () => {},
  onSelectCoachObject = () => {},
  open,
  refetch = () => {},
  selectCoachOnly = false,
  $actions,
}) => {
  const { event = {} } = eventDate || eventDates[0] || {};
  const { coachTokendecoded } = useTokenContext();
  const currentFranchiseId = coachTokendecoded?.franchiseIds[0];

  const { loading: loadingState } = useLoading();

  const [coachAssignmentOption, setCoachAssignmentOption] = useState(
    COACH_ASSIGNMENT_ENUM.ALL,
  );
  const [eventDatesToHighlight, setEventDatesToHighlight] = useState(1);

  const [selectedDates, setSelectedDates] = useState([]);
  const [bulkSelectedCoaches, setBulkSelectedCoaches] = useState([]);
  const [allowedConflicts, setAllowedConflicts] = useState(2);
  const [bufferOrOverLapTime, setBufferOrOverLapTime] = useState(0);

  const [availabilityOverlapBufferMode, setAvailabilityOverlapBufferMode] =
    useState('buffer');

  const [coachAvailabilityFilter, setCoachAvailabilityFilter] = useState({
    available: false,
    staffed: false,
    unavailable: false,
  });

  const [coachFilters, setCoachFilters] = useState({});
  const {
    isOpen: isOpenNotifySelectionModal,
    actions: notifySelectionModalActions,
    context: notifySelectionItem,
  } = useModal();

  const { data: filterData, loading: filterLoading } = useQuery(
    coachAvailabilityFiltersQuery,
    {
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: participantRolesData, loading: participantRolesLoading } =
    useQuery(participantRolesQuery, {
      fetchPolicy: 'cache-and-network',
      variables: {
        franchiseId: event?.franchiseId,
      },
    });

  const { loading: franchiseLoading } = useQuery(franchiseeGetById, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const { settings } = data?.franchise || {};

      const {
        allowedConflicts,
        availabilityOverlapBufferMode,
        bufferOrOverlapTimeForAvailability,
      } = settings;

      const bufferOrOverlapTimeFranchise =
        !isNil(bufferOrOverlapTimeForAvailability) &&
        bufferOrOverlapTimeForAvailability >= 0
          ? bufferOrOverlapTimeForAvailability
          : 0;

      const mode = availabilityOverlapBufferMode || 'buffer';

      setAvailabilityOverlapBufferMode(mode);
      setBufferOrOverLapTime(bufferOrOverlapTimeFranchise);

      if (!isNil(allowedConflicts) && allowedConflicts >= 0) {
        setAllowedConflicts(allowedConflicts);
      }
    },
  });

  const {
    data: coachAvailabilityScheduleData,
    loading: coachAvailabilityScheduleLoading,
  } = useQuery(coachAvailabilityScheduleQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      availabilityFilters: {
        eventDateId: Number(eventDate?.id),
      },
    },
  });

  const { data: classEventDatesData, loading: classEventDatesLoading } =
    useQuery(getClassEventDates, {
      fetchPolicy: 'cache-and-network',
      variables: {
        ids: eventDates?.map((el) => Number(el.event.id)) ?? [
          eventDate?.event.id,
        ],
      },
    });

  const [
    getConflictingEventDates,
    { data: conflictingEventDatesData, loading: conflictingEventDatesLoading },
  ] = useLazyQuery(conflictingEventDatesQuery, {
    fetchPolicy: 'network-only',
  });

  const [scheduleCoaches, { loading: mutationLoading }] = useMutation(
    scheduleCoachesMutation,
  );

  const { setAsyncTaskId } = useAsyncTaskStatus({
    onComplete: async () => {
      showToastMessage(
        'Coach(es) have been updated successfully, It might take a few moments to be shown on events calendar',
      );
      loadingState.stop();
      await onClose({ clean: true });
      await refetch({
        eventDatesToHighlight: eventDatesToHighlight,
        eventIdToHighlight: [Number(event.id)],
        shouldHighlightNewDates: true,
      });
    },
    onFail: (message) => {
      loadingState.stop();

      showToastMessage(message || 'Unable to update coach(es)', 'error');
    },
    poolingInterval: 1000,
  });
  const datesState = useMemo(() => {
    if (!classEventDatesData?.classEventDates[0]?.eventDates?.length) {
      return [];
    }
    return (
      classEventDatesData?.classEventDates[0]?.eventDates?.map((element) => {
        return {
          active: selectedDates.includes(element.id),
          date: element.startDateTimeInUTC,
          id: element.id,
        };
      }) || []
    );
  }, [classEventDatesData?.classEventDates?.eventDates, selectedDates]);
  const hasAvailabilityFiltersChanged = useRef(false);

  const handleClassFiltersChange = (e) => {
    hasAvailabilityFiltersChanged.current = true;
    setCoachAvailabilityFilter({
      ...coachAvailabilityFilter,
      [e.target.value]: e.target.checked,
    });
  };
  const handleFiltersChange = (key, value) => {
    setCoachFilters({
      ...coachFilters,
      [key]: value,
    });
  };
  const coachesConflicts = useMemo(() => {
    const selectedEventDates = [];
    if (conflictingEventDatesLoading) {
      return [];
    }
    if (
      selectedDates.length > 0 &&
      coachAssignmentOption === COACH_ASSIGNMENT_ENUM.SELECTED
    ) {
      selectedEventDates.push(
        ...datesState.filter((date) => selectedDates.includes(date.id)),
      );
    }
    if (coachAssignmentOption === COACH_ASSIGNMENT_ENUM.CURRENT_DATE) {
      selectedEventDates.push(eventDate);
    }
    return getCoachConflicts({
      coachAssignmentOption,
      coaches:
        coachAvailabilityScheduleData?.coachAvailabilitySchedule?.results,
      conflicts:
        conflictingEventDatesData?.conflictingEventDates?.eventDates || [],
      selectedDates: selectedEventDates,
      totalDatesCount:
        conflictingEventDatesData?.conflictingEventDates?.totalDates,
    });
  }, [
    conflictingEventDatesLoading,
    selectedDates,
    coachAssignmentOption,
    coachAvailabilityScheduleData?.coachAvailabilitySchedule?.results,
    conflictingEventDatesData?.conflictingEventDates?.eventDates,
    conflictingEventDatesData?.conflictingEventDates?.totalDates,
    datesState,
    eventDate,
  ]);

  const {
    availableCoachIds,
    items: calendarItems,
    staffedCoachIds,
    unavailableCoachIds,
  } = useMemo(() => {
    return getCalendarItems({
      allowedConflicts,
      coachAssignmentOption,
      coaches:
        coachAvailabilityScheduleData?.coachAvailabilitySchedule?.results || [],
      coachesConflicts,
      currentFranchiseId,
      eventDates: eventDates || [eventDate],
      filters: coachFilters,
    });
  }, [
    allowedConflicts,
    coachAssignmentOption,
    coachAvailabilityScheduleData?.coachAvailabilitySchedule?.results,
    coachesConflicts,
    currentFranchiseId,
    eventDates,
    eventDate,
    coachFilters,
  ]);
  const availableCoachIdsCount = availableCoachIds.length;

  const filteredCalendarItems = useMemo(() => {
    const allItems =
      (!coachAvailabilityFilter.available &&
        !coachAvailabilityFilter.staffed &&
        !coachAvailabilityFilter.unavailable) ||
      (coachAvailabilityFilter.available &&
        coachAvailabilityFilter.staffed &&
        coachAvailabilityFilter.unavailable)
        ? true
        : false;
    if (allItems) {
      return calendarItems;
    }
    const filteredItems = [];
    if (coachAvailabilityFilter.available) {
      filteredItems.push(
        ...calendarItems.filter((item) => {
          return availableCoachIds.includes(item.coach.id);
        }),
      );
    }
    if (coachAvailabilityFilter.staffed) {
      filteredItems.push(
        ...calendarItems.filter((item) => {
          return staffedCoachIds.includes(item.coach.id);
        }),
      );
    }
    if (coachAvailabilityFilter.unavailable) {
      filteredItems.push(
        ...calendarItems.filter((item) => {
          return unavailableCoachIds.includes(item.coach.id);
        }),
      );
    }
    return filteredItems;
  }, [
    coachAvailabilityFilter,
    calendarItems,
    availableCoachIds,
    staffedCoachIds,
    unavailableCoachIds,
  ]);

  useEffect(() => {
    if (hasAvailabilityFiltersChanged.current) {
      return;
    }

    setCoachAvailabilityFilter({
      available: availableCoachIdsCount > 0,
      staffed: false,
      unavailable: false,
    });
  }, [availableCoachIdsCount]);

  const categories = useMemo(() => {
    return getCategoryOptions(
      coachAvailabilityScheduleData?.coachAvailabilitySchedule?.results,
      filterData?.coachAvailabilityFilters?.categories,
    );
  }, [
    coachAvailabilityScheduleData?.coachAvailabilitySchedule?.results,
    filterData?.coachAvailabilityFilters?.categories,
  ]);

  const getLocalizedDate = (dateOnlyString) => new Date(`${dateOnlyString}`);

  const datesToHighlight =
    datesState?.length > 0
      ? [
          {
            'react-datepicker__day--selected': datesState
              .filter((date) => {
                return date?.active;
              })
              .map((date) => getLocalizedDate(date?.date)),
          },
          {
            'react-datepicker__day--disabled': datesState
              .filter((date) => {
                return !date?.active;
              })
              .map((date) => getLocalizedDate(date?.date)),
          },
        ]
      : [];

  const handleSelectedDates = (date) => {
    const dateValue = datesState.find((element) =>
      isSameDay(new Date(element.date), date),
    );
    if (dateValue?.id) {
      setSelectedDates((previous) => {
        if (dateValue.active) {
          return previous.filter((element) => element !== dateValue.id);
        }
        return [...previous, dateValue.id];
      });
    }
  };

  const participantRoles = useMemo(() => {
    return sortBy([...(participantRolesData?.participantRoles || [])], 'title');
  }, [participantRolesData?.participantRoles]);

  const saveCoaches = async (properties = {}) => {
    loadingState.start();

    try {
      const input = getCoachAssignmentInput({
        assignedStaff: properties.assignedStaff,
        coachAssignmentOption,
        datesState,
        eventDate,
        notifications: properties.notifications,
        selectedDates,
        setEventDatesToHighlight,
      });
      const response = await scheduleCoaches({
        variables: {
          input: [input],
        },
      });

      if (response.data?.scheduleCoaches?.id) {
        setAsyncTaskId(response.data?.scheduleCoaches?.id);
        return;
      }
      throw new Error('Unable to update coach(es)');
    } catch {
      loadingState.stop();
      showToastMessage('Unable to update coach(es)', 'error');
    }
  };

  const handleBulkSelectedCoach = (coach) => {
    const existingIndex = bulkSelectedCoaches.findIndex(
      (item) => item.id === coach.id,
    );
    if (existingIndex > -1) {
      setBulkSelectedCoaches((previous) => {
        return previous.filter((item) => item.id !== coach.id);
      });
    } else {
      setBulkSelectedCoaches((previous) => {
        return [...previous, coach];
      });
    }
  };

  useEffect(() => {
    const debouncedRefetch = debounce(() => {
      if (
        !coachAvailabilityScheduleData?.coachAvailabilitySchedule?.results
          ?.length ||
        franchiseLoading
      ) {
        return;
      }
      getConflictingEventDates({
        variables: {
          input: {
            availabilityOverlapBufferMode,
            bufferOrOverlapTime: Number(bufferOrOverLapTime),
            coaches:
              coachAvailabilityScheduleData?.coachAvailabilitySchedule?.results?.map(
                (coach) => coach?.id,
              ),
            eventDateId: Number(eventDate?.id),
            eventIds: eventDates.map((el) => Number(el?.event?.id)),
          },
        },
      });
    }, 500);

    debouncedRefetch();

    return () => debouncedRefetch.cancel();
  }, [
    availabilityOverlapBufferMode,
    coachAvailabilityScheduleData?.coachAvailabilitySchedule?.results,
    eventDate?.id,
    franchiseLoading,
    getConflictingEventDates,
    bufferOrOverLapTime,
    eventDates,
  ]);
  const isLoading =
    filterLoading ||
    coachAvailabilityScheduleLoading ||
    participantRolesLoading ||
    mutationLoading ||
    loadingState.get ||
    conflictingEventDatesLoading;
  const isMobile = useMediaQuery(
    [breakpoints.mobile, breakpoints.tablet, breakpoints.desktop],
    [true, true, false],
  );

  return (
    <ModalWrapper $actions={$actions}>
      <div className={'findCoachWrapper'}>
        <FindCoachHeader
          allowedConflicts={allowedConflicts}
          availabilityOverlapBufferMode={availabilityOverlapBufferMode}
          availableCoachesCount={availableCoachIds.length}
          bufferOrOverLapTime={bufferOrOverLapTime}
          coachAvailabilityFilter={coachAvailabilityFilter}
          eventDate={eventDate}
          handleClassFiltersChange={handleClassFiltersChange}
          hideAllowedConflictsInput={
            coachAssignmentOption === COACH_ASSIGNMENT_ENUM.CURRENT_DATE
          }
          isBulk={isBulk}
          onClose={onClose}
          setAllowedConflicts={setAllowedConflicts}
          setAvailabilityOverlapBufferMode={setAvailabilityOverlapBufferMode}
          setBufferOrOverLapTime={setBufferOrOverLapTime}
          staffedCoachCount={staffedCoachIds.length}
          unavailableCoachCount={unavailableCoachIds.length}
        />
        <BlockLoading loading={isLoading}>
          <div className="details">
            <div className="filters">
              <div className="tw-w-[85%] sm:tw-w-full">
                {isMobile ? (
                  <TextField
                    disabled={classEventDatesLoading}
                    fullWidth
                    onChange={(e) => setCoachAssignmentOption(e.target.value)}
                    select
                    value={coachAssignmentOption}
                  >
                    {COACH_ASSIGNMENT_ACTION_ITEMS.map((action) => (
                      <MenuItem key={action.id} value={action.id}>
                        {action.text}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  !isBulk && (
                    <div>
                      <div>
                        {COACH_ASSIGNMENT_ACTION_ITEMS?.map((action) => {
                          return (
                            <div
                              className="coachAssignmentActionRadio"
                              disabled={classEventDatesLoading}
                              key={action.id}
                              onClick={() => {
                                if (classEventDatesLoading) {
                                  return;
                                }
                                setCoachAssignmentOption(action.id);
                              }}
                              style={{
                                ...(action.id === coachAssignmentOption && {
                                  borderColor: '#02AFEE',
                                }),
                              }}
                            >
                              <div className="labelInput">
                                <label id={action.id}>{action.text}</label>
                                <input
                                  type="radio"
                                  checked={action.id === coachAssignmentOption}
                                  disabled={classEventDatesLoading}
                                  id={action.id}
                                  key={action.id}
                                  name="action"
                                  value={action.id}
                                />
                              </div>
                              {coachAssignmentOption ===
                                COACH_ASSIGNMENT_ENUM.SELECTED &&
                                action.id ===
                                  COACH_ASSIGNMENT_ENUM.SELECTED && (
                                  <div className="datePickerContainer">
                                    <div css={datePickerStyle}>
                                      <ReactDatePicker
                                        calendarClassName="aa-datepicker datepicker-class-schedule"
                                        disabledKeyboardNavigation
                                        highlightDates={datesToHighlight}
                                        inline
                                        onSelect={(date) => {
                                          handleSelectedDates(date);
                                        }}
                                        openToDate={new Date()}
                                        showDisabledMonthNavigation
                                      />
                                    </div>
                                  </div>
                                )}
                            </div>
                          );
                        })}
                        <span className="error"></span>
                      </div>
                    </div>
                  )
                )}
              </div>
              {filterData?.coachAvailabilityFilters && (
                <CoachFilter
                  categories={categories}
                  coaches={filterData?.coachAvailabilityFilters?.coaches}
                  filters={coachFilters}
                  onChange={handleFiltersChange}
                />
              )}
            </div>
            <DayCalendar
              emptyMessage="No coaches exists for selected filters. Please try different filters."
              eventCell={
                <EventDetails
                  allowedConflicts={
                    coachAssignmentOption === COACH_ASSIGNMENT_ENUM.CURRENT_DATE
                      ? 0
                      : allowedConflicts
                  }
                  coaches={
                    coachAvailabilityScheduleData?.coachAvailabilitySchedule
                      ?.results || []
                  }
                  coachesConflicts={coachesConflicts}
                  currentEventId={event.id}
                  disableActions={
                    selectedDates.length === 0 &&
                    coachAssignmentOption === COACH_ASSIGNMENT_ENUM.SELECTED
                  }
                  eventDates={eventDates || [eventDate]}
                  isBulk={isBulk}
                  onStaff={(coaches, item = null) => {
                    console.log('onStaff is working', coaches);
                    if (selectCoachOnly) {
                      onSelectCoach(coaches.map((coach) => coach.id));
                      onSelectCoachObject(coaches, item);
                      return;
                    }
                    notifySelectionModalActions.open(coaches);
                  }}
                />
              }
              eventDates={eventDates || [eventDate]}
              isLoading={isLoading}
              itemCell={
                <CoachDetails
                  coachesConflicts={coachesConflicts}
                  disableActions={
                    selectedDates.length === 0 &&
                    coachAssignmentOption === COACH_ASSIGNMENT_ENUM.SELECTED
                  }
                  eventDates={eventDates || [eventDate]}
                  handleSelect={handleBulkSelectedCoach}
                  isBulk={isBulk}
                  selectedCoaches={bulkSelectedCoaches}
                />
              }
              items={filteredCalendarItems}
              itemsTitle={
                <div className="itemsTitle">
                  {availableCoachIds.length > 0 && (
                    <span>Available Coaches ({availableCoachIds.length})</span>
                  )}
                  {staffedCoachIds.length > 0 && (
                    <span>Staffed Coaches ({staffedCoachIds.length})</span>
                  )}
                  {unavailableCoachIds.length > 0 && (
                    <span>
                      Unavailable Coaches ({unavailableCoachIds.length})
                    </span>
                  )}
                </div>
              }
            />
          </div>
        </BlockLoading>
        {bulkSelectedCoaches.length > 0 ? (
          <div className={'bottomActionBar'}>
            <p>Select Coach to staff</p>
            <div className={'buttonContainer'}>
              <Button
                onClick={() => setBulkSelectedCoaches([])}
                style={{
                  background: '#193D86',
                  borderRadius: '5px',
                  marginRight: '8px',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (selectCoachOnly) {
                    onSelectCoach(bulkSelectedCoaches.map((coach) => coach.id));
                    onSelectCoachObject(bulkSelectedCoaches);
                    return;
                  }
                  notifySelectionModalActions.open(bulkSelectedCoaches);
                }}
                style={{
                  background: '#02AFEE',
                  borderRadius: '5px',
                }}
              >
                Staff
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      {isOpenNotifySelectionModal && (
        <NotifySelection
          coachesConflicts={coachesConflicts}
          defaultRole={participantRoles.find((role) => role.isDefaultRole)}
          onClose={() => notifySelectionModalActions.close()}
          onSubmit={saveCoaches}
          open={true}
          participantRoles={participantRoles}
          selectedCoaches={notifySelectionItem}
          showCheckboxCoachOnDetails={
            coachAssignmentOption === COACH_ASSIGNMENT_ENUM.ALL
          }
        />
      )}
    </ModalWrapper>
  );
};
